import React, { useState } from "react";
import { Container, Paper } from "@mui/material";
import { useAuth } from "../../contexts/AuthContext";
import HybridNav from "../shared/HybridNav";
import ManageUsers from "../admin/ManageUsers";
import NewsDashboard from "../news/NewsDashboard";
import NewsSubmission from "../reporter/NewsSubmission";
import ManageHomepageLayout from "../chiefeditor/ManageHomepageLayout";
import ArchivedArticles from "../ArchivedArticles";
import log from "../../utils/logger";

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const [currentPath, setCurrentPath] = useState<string>("news-submission");

  // Debug logging
  React.useEffect(() => {
    log.debug("Dashboard - User state:", user);
    log.debug("Dashboard - User role:", user?.role);
  }, [user]);

  const handleNavigate = (path: string) => {
    setCurrentPath(path);
  };

  const handleNewsSuccess = (navigateTo?: string) => {
    log.debug("handleNewsSuccess called with:", navigateTo);
    // Reload counts in navigation
    if ((window as any).__reloadCounts) {
      (window as any).__reloadCounts();
    }
    // Navigate to drafts or specified path
    if (navigateTo) {
      log.debug("Navigating to:", navigateTo);
      setCurrentPath(navigateTo);
    }
  };

  const isAdmin = user?.role === "admin";
  const isChiefEditor = user?.role === "chief-editor" || isAdmin;
  const isCoEditor = user?.role === "co-editor" || isChiefEditor;
  const canEditNews = user?.role === "reporter" || isCoEditor;

  // Check if current view should use full width
  const useFullWidth = currentPath === "manage-homepage";

  const renderContent = () => {
    switch (currentPath) {
      case "manage-users":
        return isAdmin || isChiefEditor ? <ManageUsers /> : null;
      case "news-submission":
        return canEditNews ? <NewsSubmission onSuccess={handleNewsSuccess} /> : null;
      case "drafts":
        return canEditNews ? <NewsDashboard viewType="drafts" onNavigate={handleNavigate} /> : null;
      case "in-review":
        return canEditNews ? <NewsDashboard viewType="in-review" onNavigate={handleNavigate} /> : null;
      case "ready-to-publish":
        return canEditNews ? <NewsDashboard viewType="ready-to-publish" onNavigate={handleNavigate} /> : null;
      case "live":
        return <NewsDashboard viewType="published" onNavigate={handleNavigate} />;
      case "manage-homepage":
        return isChiefEditor ? <ManageHomepageLayout /> : null;
      case "archived":
        return isCoEditor ? <ArchivedArticles /> : null;
      default:
        return canEditNews ? <NewsSubmission onSuccess={handleNewsSuccess} /> : null;
    }
  };

  return (
    <HybridNav currentPath={currentPath} onNavigate={handleNavigate}>
      {useFullWidth ? (
        // Full width layout for NewspaperEditor
        <Paper elevation={3} sx={{ padding: 3, margin: 2 }}>
          {renderContent()}
        </Paper>
      ) : (
        // Constrained width for other views
        <Container maxWidth="lg">
          <Paper elevation={3} sx={{ padding: 4 }}>
            {renderContent()}
          </Paper>
        </Container>
      )}
    </HybridNav>
  );
};

export default Dashboard;

